import { createApiClient } from './client'

export type ModificationCategory = 
  | 'armor'
  | 'suspension' 
  | 'recovery'
  | 'lighting'
  | 'storage'
  | 'electrical'
  | 'tires_wheels'
  | 'roof_rack'
  | 'roof_top_tent'
  | 'truck_camper'
  | 'other'

export interface VehicleModification {
  id: string
  vehicle_id: string
  category: ModificationCategory
  brand_id?: string
  custom_brand_name?: string
  product_name: string
  product_model?: string
  description?: string
  purchase_price?: number
  notes?: string
  created_at: string
  updated_at: string
  // Joined data
  brand_name?: string
  brand_website?: string
  logo_url?: string
}

export interface CreateVehicleModificationDto {
  vehicle_id: string
  category: ModificationCategory
  brand_id?: string
  custom_brand_name?: string
  product_name: string
  product_model?: string
  description?: string
  purchase_price?: number
  notes?: string
}

export interface UpdateVehicleModificationDto {
  category?: ModificationCategory
  brand_id?: string
  custom_brand_name?: string
  product_name?: string
  product_model?: string
  description?: string
  purchase_price?: number
  notes?: string
}

export interface GearBrand {
  id: string
  name: string
  category: ModificationCategory
  website?: string
  logo_url?: string
  description?: string
  is_partner: boolean
  partner_tier?: 'bronze' | 'silver' | 'gold' | 'platinum'
  created_at: string
}

export interface ModificationStats {
  totalModifications: number
  totalInvestment: number
  categories: { [key: string]: number }
}

export interface VehicleModificationApiResponse {
  success: boolean
  data: VehicleModification
}

export interface VehicleModificationListApiResponse {
  success: boolean
  data: VehicleModification[]
}

export interface GearBrandListApiResponse {
  success: boolean
  data: GearBrand[]
}

export interface ModificationStatsApiResponse {
  success: boolean
  data: ModificationStats
}

export class VehicleModificationApi {
  private apiClient: ReturnType<typeof createApiClient>

  constructor(getAuthToken: () => Promise<string | null>) {
    this.apiClient = createApiClient(getAuthToken)
  }

  // Get all modifications for a vehicle
  async getVehicleModifications(vehicleId: string): Promise<VehicleModification[]> {
    const response = await this.apiClient.get<VehicleModificationListApiResponse>(`/vehicles/${vehicleId}/modifications`)
    return response.data
  }

  // Get modification by ID
  async getModification(modificationId: string): Promise<VehicleModification> {
    const response = await this.apiClient.get<VehicleModificationApiResponse>(`/modifications/${modificationId}`)
    return response.data
  }

  // Create new modification
  async createModification(modificationData: CreateVehicleModificationDto): Promise<VehicleModification> {
    const response = await this.apiClient.post<VehicleModificationApiResponse>('/modifications', modificationData)
    return response.data
  }

  // Update modification
  async updateModification(modificationId: string, modificationData: UpdateVehicleModificationDto): Promise<VehicleModification> {
    const response = await this.apiClient.put<VehicleModificationApiResponse>(`/modifications/${modificationId}`, modificationData)
    return response.data
  }

  // Delete modification
  async deleteModification(modificationId: string): Promise<void> {
    await this.apiClient.delete(`/modifications/${modificationId}`)
  }

  // Get gear brands by category
  async getGearBrandsByCategory(category: ModificationCategory): Promise<GearBrand[]> {
    const response = await this.apiClient.get<GearBrandListApiResponse>(`/gear-brands/category/${category}`)
    return response.data
  }

  // Get all gear brands
  async getAllGearBrands(): Promise<GearBrand[]> {
    const response = await this.apiClient.get<GearBrandListApiResponse>('/gear-brands')
    return response.data
  }

  // Get modification statistics
  async getModificationStats(vehicleId: string): Promise<ModificationStats> {
    const response = await this.apiClient.get<ModificationStatsApiResponse>(`/vehicles/${vehicleId}/modifications/stats`)
    return response.data
  }
}
